import { useTranslation } from "react-i18next";
import { useContext, useEffect } from "react";
import CampaignContextProvider from "../../../context/campaign/provider";
import CampaignContext from "../../../context/campaign/context";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Descriptions, Divider, Image, Progress, Row, Tag } from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import styles from "../../charity/charity-details/style.module.scss";
import Spin from "../../general/antd/spin";
import {
    CampaignStatus,
    getDonationGoalName,
    getPeriodicTypeName,
    getCampaignStatusName,
    getColor
} from "../../../models/campaign/enums";
import moment from "moment";

const Details = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(CampaignContext);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    if (loading.includes("details")) {
        return (
            <div style={{ height: "60vh" }}>
                <Spin />
            </div>
        );
    }

    if (!details) {
        return null;
    }

    const progressPercentage = details.targetAmount && details.paidAmount 
        ? Math.min(100, (parseFloat(details.paidAmount) / parseFloat(details.targetAmount)) * 100)
        : 0;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(-1)}
                >
                    {t("back")}
                </Button>
                <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/campaigns/update/${details.id}`)}
                >
                    {t("edit")}
                </Button>
            </div>

            <Card className={styles.card}>
                <div className={styles.title}>{details.name}</div>
                <Divider />

                <Row gutter={[24, 24]}>
                    {/*<Col span={24}>*/}
                    {/*    <div className={styles.imageContainer}>*/}
                    {/*        {details.cover && (*/}
                    {/*            <Image*/}
                    {/*                src={details.cover.url}*/}
                    {/*                alt={details.name}*/}
                    {/*                className={styles.coverImage}*/}
                    {/*            />*/}
                    {/*        )}*/}
                    {/*    </div>*/}
                    {/*</Col>*/}

                    <Col span={24}>
                        <Card title={t("campaign_progress")} className={styles.subCard}>
                            <Progress 
                                percent={progressPercentage}
                                status={progressPercentage >= 100 ? "success" : "active"} 
                            />
                            <div className={styles.progressInfo}>
                                <span>{t("paid_amount")}: {details.paidAmount}</span>
                                <Divider key={1} type="vertical"/>,
                                <span>{t("target_amount")}: {details.targetAmount}</span>
                            </div>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title={t("basic_information")} className={styles.subCard}>
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label={t("id")}>{details.id}</Descriptions.Item>
                                <Descriptions.Item label={t("case_number")}>{details.caseNumber}</Descriptions.Item>
                                <Descriptions.Item label={t("status")}>
                                    <Tag color={
                                        Number(details.status) === CampaignStatus.COMPLETED ? "success" :
                                        Number(details.status) === CampaignStatus.COLLECTING_DONATIONS ? "processing" :
                                        Number(details.status) === CampaignStatus.FINISHED ? "warning" : "default"
                                    }>
                                        {t(getCampaignStatusName(Number(details?.status)))}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t("donation_goal")}>
                                    {t(getDonationGoalName(details.donationGoal))}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("periodic")}>
                                    {details.periodic ? t("yes") : t("no")}
                                </Descriptions.Item>

                                {details.periodic && (
                                    <Descriptions.Item label={t("periodic_type")}>
                                        {details.periodicType && details.periodicType.length > 0 ? (
                                            details.periodicType.map((type: number) => (
                                                <Tag color={getColor(type)} key={type}>
                                                    {getPeriodicTypeName(type)}
                                                </Tag>
                                            ))
                                        ) : (
                                            t("not_available")
                                        )}
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={t("start_date")}>
                                    {moment(details.startDate).format("YYYY-MM-DD")}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("end_date")}>
                                    {moment(details.endDate).format("YYYY-MM-DD")}
                                </Descriptions.Item>
                                {!details.donationUnit && (
                                    <Descriptions.Item label={t("donation_unit")}>
                                        {details.donationUnit}
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={t("charity")}>
                                    {details.charity?.name}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("category")}>
                                    {details.category?.name}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("city")}>
                                    {details.city?.name}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title={t("contact_information")} className={styles.subCard}>
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label={t("responsible")}>{details.responsible}</Descriptions.Item>
                                <Descriptions.Item label={t("mobile")}>{details.mobile}</Descriptions.Item>
                                <Descriptions.Item label={t("telegram")}>{details.telegram}</Descriptions.Item>
                                <Descriptions.Item label={t("whatsApp")}>{details.whatsApp}</Descriptions.Item>
                                <Descriptions.Item label={t("facebook")}>{details.facebook}</Descriptions.Item>
                                <Descriptions.Item label={t("instagram")}>{details.instagram}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title={t("description")} className={styles.subCard}>
                            <div className={styles.description}>{details.description}</div>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title={t("content")} className={styles.subCard}>
                            <div className={styles.content} dangerouslySetInnerHTML={{ __html: details.content }} />
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title={t("images")} className={styles.subCard}>
                            <Row gutter={[16, 16]}>
                                {details.image && (
                                    <Col xs={24} sm={12} md={8}>
                                        <Image
                                            src={details.image.url}
                                            alt={details.name}
                                            className={styles.image}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

const CampaignDetails = () => {
    return (
        <CampaignContextProvider>
            <Details />
        </CampaignContextProvider>
    );
};

export default CampaignDetails;